import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  Component,
  ChangeDetectionStrategy,
  OnChanges
} from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CpqPopoverConfig } from './cpq-popover.model';

@Component({
  selector: 'cpq-popover-text',
  template: `<div class="cpq-popover-text">{{ text }}</div>`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PopoverTextComponent {
  text!: string;
}

@Directive({
  selector: '[cpqPopover]',
  standalone: false
})
export class PopoverDirective implements OnChanges, OnDestroy {
  @Input('cpqPopover') cpqPopover: Partial<CpqPopoverConfig>;

  private overlayRef?: OverlayRef;
  private outsideClickSub?: Subscription;

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef
  ) { }

  ngOnChanges() {
    this.cpqPopover = Object.assign(new CpqPopoverConfig(), this.cpqPopover);
  }

  @HostListener('click', ['$event'])
  toggle(event: MouseEvent) {
    event.stopPropagation();
    this.overlayRef ? this.close() : this.open();
  }

  open() {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([this.getPosition()])
      .withPush(false);

    const panelClasses: string[] = ['cpq-popover'];

    if (this.cpqPopover.isError) {
      panelClasses.push('error-text');
    }

    if (this.cpqPopover.panelClass) {
      if (Array.isArray(this.cpqPopover.panelClass)) {
        panelClasses.push(...this.cpqPopover.panelClass);
      } else {
        panelClasses.push(this.cpqPopover.panelClass);
      }
    }

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass: panelClasses
    });

    if (this.cpqPopover.content instanceof TemplateRef) {
      this.overlayRef.attach(new TemplatePortal(this.cpqPopover.content, this.viewContainerRef));
    } else {

      const portal = new ComponentPortal(PopoverTextComponent, this.viewContainerRef);
      const ref = this.overlayRef.attach(portal);
      ref.instance.text = this.cpqPopover.content;
    }

    if (this.cpqPopover.outsideClick) {
      this.outsideClickSub = this.overlayRef.outsidePointerEvents().subscribe(() => {
        this.close();
      });
    }
  }

  close() {
    this.outsideClickSub?.unsubscribe();
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }

  ngOnDestroy() {
    this.close();
  }

  private getPosition(): ConnectedPosition {
    switch (this.cpqPopover.placement) {
      case 'bottom':
        return { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 };
      case 'left':
        return { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 };
      case 'right':
        return { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 };
      default:
        return { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 };
    }
  }
}
